import React from 'react';
import { useRoleView } from '@/contexts/RoleViewContext';
import { cn } from '@/utils/cn';
import {
  Edit3,
  CheckCircle,
  Eye,
  Shield,
  Briefcase,
  ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const viewConfig = {
  preparer: {
    label: 'Preparer View',
    icon: Edit3,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    description: 'Prepare and edit reconciliations'
  },
  approver: {
    label: 'Approver View',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    description: 'Review and approve reconciliations'
  },
  full: {
    label: 'Full View',
    icon: Briefcase,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    description: 'All your reconciliations'
  },
  admin: {
    label: 'Admin View',
    icon: Shield,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    description: 'System administration'
  },
  auditor: {
    label: 'Auditor View',
    icon: Eye,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    description: 'Read-only audit access'
  }
};

export const ViewSelector: React.FC = () => {
  const { activeView, setActiveView, availableViews, viewCounts } = useRoleView();

  if (availableViews.length <= 1) {
    // Don't show selector if user only has one view
    return null;
  }

  const currentConfig = viewConfig[activeView];
  const Icon = currentConfig.icon;

  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded-md border transition-colors text-sm",
            "hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500",
            "bg-white border-gray-300"
          )}>
            <Icon className={cn("h-4 w-4", currentConfig.color)} />
            <span className="font-medium text-gray-700">{currentConfig.label}</span>
            {viewCounts[activeView] > 0 && (
              <span className={cn(
                "ml-1 px-1.5 py-0.5 text-xs font-semibold rounded-full",
                activeView === 'preparer' && "bg-blue-100 text-blue-700",
                activeView === 'approver' && "bg-green-100 text-green-700",
                activeView === 'full' && "bg-purple-100 text-purple-700",
                activeView === 'admin' && "bg-red-100 text-red-700",
                activeView === 'auditor' && "bg-gray-100 text-gray-700"
              )}>
                {viewCounts[activeView]}
              </span>
            )}
            <ChevronDown className="h-4 w-4 ml-1 text-gray-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {availableViews.map((view) => {
            const config = viewConfig[view];
            const ViewIcon = config.icon;
            const isActive = view === activeView;
            
            return (
              <DropdownMenuItem
                key={view}
                onClick={() => setActiveView(view)}
                className={cn(
                  "cursor-pointer",
                  isActive && "bg-gray-50"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "p-1.5 rounded-md",
                    config.bgColor
                  )}>
                    <ViewIcon className={cn("h-4 w-4", config.color)} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{config.label}</span>
                      {viewCounts[view] > 0 && (
                        <span className={cn(
                          "px-2 py-0.5 text-xs font-medium rounded-full",
                          view === activeView ? "bg-gray-200 text-gray-700" : "bg-gray-100 text-gray-600"
                        )}>
                          {viewCounts[view]}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {config.description}
                    </p>
                  </div>
                  {isActive && (
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                    </div>
                  )}
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

